import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Row, Col } from 'reactstrap';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import {
  CONDITIONS,
  CONDITIONS_FOR_TABLE_DISPLAY,
  TRADE_IN_VALUE,
  TRANSACTION_TYPE_SELECT_LABELS,
} from 'site-modules/shared/constants/appraisal/appraisal';

import './appraisal-report-step-bar-chart.scss';

export function AppraisalReportStepBarChart({
  prices = [],
  selectedType = TRADE_IN_VALUE,
  selectedCondition = CONDITIONS.AVERAGE,
}) {
  const selectedTransactionTypeLabel = TRANSACTION_TYPE_SELECT_LABELS[selectedType];
  const selectedTransactionTypeLabelLowerCased = selectedTransactionTypeLabel?.toLowerCase();

  // Prepare data for each condition bar
  const conditionPricesData = prices.map((price, index) => {
    const { title, text } = CONDITIONS_FOR_TABLE_DISPLAY[index];
    return { price: getPriceStringV2(price), title, text };
  });

  // Create a map for easy lookup of prices by condition title
  const priceByCondition = conditionPricesData.reduce((acc, { price, title }) => {
    acc[title] = price;
    return acc;
  }, {});

  // Construct the aria-label for accessibility
  const selectedConditionPrice = priceByCondition[selectedCondition] || 'N/A';
  let ariaLabel = `${selectedTransactionTypeLabel
    ?.replace('Value', '')
    ?.trim()} Appraisal Report Summary: Based on your answers, your vehicle is considered ${selectedCondition}. The ${selectedTransactionTypeLabelLowerCased} in the ${selectedCondition} condition is ${selectedConditionPrice}.`;

  // Add information for other conditions
  const otherConditions = Object.keys(priceByCondition).filter(title => title !== selectedCondition);

  if (otherConditions.length > 0) {
    ariaLabel += otherConditions
      .map(title => {
        const price = priceByCondition[title];
        return ` If your vehicle was in ${title} condition the value would be ${price}.`;
      })
      .join('');
  }

  return (
    !!prices.length && (
      <div
        className="appraisal-report-bar-chart pt-2_5 pt-sm-1_5"
        role="img"
        aria-label={ariaLabel}
        data-tracking-parent="edmunds-appraisal-report-bar-chart"
        aria-live="polite"
        aria-atomic="true"
      >
        <Row className="bar-chart justify-content-center text-center text-gray-darker align-items-end size-14">
          {conditionPricesData.map(({ price, title, text }, index) => {
            const barKey = `appraisal-report-bar-chart-bar-${index}`;
            const isSelectedCondition = title === selectedCondition;

            return (
              <Col
                key={barKey}
                xs={3}
                className={classnames(
                  'bar d-flex align-items-end justify-content-center pb-0_5 mx-0_25 mx-lg-0_5 pos-r',
                  `bar-${index}`,
                  selectedType,
                  {
                    'selected-condition fw-bold text-cool-gray-10': isSelectedCondition,
                  }
                )}
              >
                {price}
                <div className="bar-description size-12 text-gray-darker text-center px-0 px-lg-05 mx-0_25 mx-lg-0_5 d-flex align-items-end">
                  <span
                    className={classnames({
                      'fw-bold selected-condition-title': isSelectedCondition,
                    })}
                  >
                    {title}
                  </span>{' '}
                  <PopoverItem
                    className="appraisal-table-info-btn"
                    iconClassName="small text-gray-darker"
                    placement="bottom"
                    screenReaderTooltipLabel={`${title} condition information`}
                  >
                    {text}
                  </PopoverItem>
                </div>
              </Col>
            );
          })}
        </Row>
        <hr className="bar-chart-underline mt-0 mb-2" />
      </div>
    )
  );
}

AppraisalReportStepBarChart.propTypes = {
  prices: PropTypes.arrayOf(PropTypes.number),
  selectedType: PropTypes.string,
  selectedCondition: PropTypes.string,
};
